/**
 * @file experiences.js
 * @description Constants used by Experiences feature: options, lists and table field definitions.
 *
 * This file keeps a `commons` export for backward compatibility but defines
 * individual constants in UPPER_SNAKE_CASE for clarity and reuse.
 */

/**
 * Availability options.
 * Previous name: availability
 * @type {{label: string, value: string}[]}
 */
export const AVAILABILITY_OPTIONS = [
    { label: "Default", value: "DEFAULT" },
    { label: "Blackout Dates", value: "BLACKOUT" },
    { label: "No Restriction", value: "UNRESTRICRED" }
];

/**
 * Cut-off options for experiences.
 * Previous name: cutOffOptios
 * @type {{label: string, value: string}[]}
 */
export const CUTOFF_OPTIONS = [
    { label: "Default", value: "DEFAULT" },
    { label: "Minimum", value: "MINIMUM" },
    { label: "No Restriction", value: "UNRESTRICRED" }
];

/**
 * Product types available for experiences.
 * Previous name: productTypes
 * @type {{label: string, value: string, disabled: boolean}[]}
 */
export const PRODUCT_TYPES = [
    {
        label: "Private Transfer",
        value: "PRIVATE_TRANSFER",
        disabled: false
    },
    { label: "Tour", value: "TOUR", disabled: false }
];

/**
 * Inclusion items presented to the user.
 * Previous name: inclusions
 * @type {{text?: string, value?: string}[]}
 */
export const INCLUSIONS = [
    { text: "Flight tracking", value: "flight tracking" },
    {
        text: "15-minutes waiting time",
        value: "15-minutes waiting time"
    },
    {
        text: "30-minutes waiting time",
        value: "30-minutes waiting time"
    },
    {
        text: "60-minutes waiting time",
        value: "60-minutes waiting time"
    },
    {
        text: "Bottle of water",
        value: "bottle of water"
    },
    {
        text: "Tolls and taxes",
        value: "tolls and taxes"
    },
    {
        value: "gratuity",
        text: "Gratuity"
    },
    {
        value: "meet & greet",
        text: "Meet & greet"
    },
    {
        text: "Additional items of luggage",
        value: "additional items of luggage"
    }
];

/**
 * Exclusion items presented to the user.
 * Previous name: exclusions
 * @type {{value: string, text: string}[]}
 */
export const EXCLUSIONS = [
    {
        value: "food",
        text: "Food"
    },
    {
        value: "drinks",
        text: "Drinks"
    },
    {
        value: "additional items of luggage",
        text: "Additional items of luggage"
    },
    {
        value: "gratuity",
        text: "Gratuity"
    },
    {
        value: "parking fees",
        text: "Parking Fees"
    },
    {
        value: "meet & greet",
        text: "Meet & greet"
    },
    {
        value: "extra stop",
        text: "Extra Stop"
    },
    {
        value: "extra waiting time",
        text: "Extra waiting time"
    },
    {
        value: "child seats",
        text: "Child seats"
    }
];

/**
 * Supported currencies and symbols.
 * Previous name: coins
 * @type {{code: string, symbol: string, label: string}[]}
 */
export const CURRENCY_COINS = [
    { code: "USD", symbol: "$", label: "US Dollar" },
    { code: "EUR", symbol: "€", label: "Euro" },
    { code: "GBP", symbol: "£", label: "British Pound" },
    { code: "CHF", symbol: "₣", label: "Swiss Franc" },
    { code: "DKK", symbol: "kr", label: "Denmark Krone" },
    { code: "SEK", symbol: "kr", label: "Swedish Krona" },
    { code: "NOK", symbol: "kr", label: "Norwegian Krone" },
    { code: "ISK", symbol: "kr", label: "Icelandic Króna" },
    { code: "AED", symbol: "د.إ", label: "United Arab Emirates Dirham" }
];

/**
 * Dropzone configuration options used by file upload components.
 * Previous name: dropzoneOptions
 * @type {Object}
 */
export const DROPZONE_OPTIONS = {
    url: "https://httpbin.org/post",
    thumbnailWidth: 200,
    thumbnailHeight: 200,
    maxFilesize: 256,
    maxFiles: 5,
    acceptedFiles: "image/png, image/jpeg",
    addRemoveLinks: true,
    thumbnailMethod: "crop",
    uploadMultiple: true,
    includeStyling: false,
    dictDefaultMessage:
        "<i class='flaticon-arrow-up'></i><br>Drop files here or click to upload",
    dictRemoveFile: "<i class='flaticon-delete'></i>"
};

/**
 * Minimum selection values for forms (1..24).
 * Previous name: minimumSelect
 * @type {number[]}
 */
export const MIN_SELECT_OPTIONS = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
    13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24
];

/**
 * Custom rich-text toolbar configuration.
 * Previous name: customToolbar
 * @type {Array}
 */
export const CUSTOM_TOOLBAR = [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [
        { align: "" },
        { align: "center" },
        { align: "right" },
        { align: "justify" }
    ],
    ["link", "clean"]
];

/**
 * Transfer type options for experiences.
 * Previous name: transfersOptions
 * @type {{value: string, label: string}[]}
 */
export const TRANSFERS_OPTIONS = [
    { value: "ARRIVAL", label: "Airport (Arrival)" },
    { value: "DEPARTURE", label: "Airport (Departure)" },
    { value: "STATION", label: "Station" },
    { value: "PORT", label: "Port" },
    { value: "JOURNEY", label: "Journey" }
];

/**
 * Table fields configuration for experiences list view.
 * Previous name: fields
 * @type {Object[]}
 */
export const EXPERIENCE_FIELDS = [
    {
        key: "name",
        label: "Name",
        sortable: true,
        thStyle: { minWidth: "160px" },
        class: "text-left"
    },
    {
        key: "from",
        label: "From",
        sortable: true,
        thStyle: { minWidth: "160px" },
        class: "text-left"
    },
    {
        key: "end",
        label: "End",
        sortable: true,
        thStyle: { minWidth: "160px" },
        class: "text-left"
    },
    {
        key: "status",
        label: "Status",
        sortable: true,
        thStyle: { minWidth: "110px" },
        class: "alinmov6"
    },
    {
        key: "actions",
        label: "Actions",
        sortable: false,
        class: "alinmov6",
        thStyle: { minWidth: "120px" }
    }
];